console.log("VPX OPTIMIZER FILE LOADED");

(function () {

    if (!window.VORYNEX) {
        console.error("VORYNEX namespace not found. Optimizer aborted.");
        return;
    }

    if (!window.VORYNEX.foods) {
        console.error("vpx-foods.js must load before vpx-optimizer.js. Optimizer aborted.");
        return;
    }

    const FOODS = window.VORYNEX.foods;

    /*
    ==========================================
    How this works
    ------------------------------------------
    The meal generator scales whole-meal templates
    to hit total calories, but doesn't separately
    target protein/carbs/fat — so the day's macro
    split can drift from the user's targets even
    though calories land correctly.

    This optimizer runs 3 passes (protein, then
    carbs, then fat). In each pass it:
      1) Sums how much of that macro already comes
         from foods OUTSIDE the category being
         adjusted (e.g. the carbs already present
         in protein sources like whey or oats).
      2) Scales every food item IN that category by
         a single factor so category + fixed
         contribution lands on the daily target.

    Order matters: protein first (most important
    for training goals), then carbs, then fat last
    — fat absorbs most of the small residual error,
    which is the standard coaching convention.

    This is a proportional-scaling approach, not a
    full combinatorial search — it won't always hit
    the spec's ±1g ideal, but gets meaningfully
    closer than unscaled templates, with no risk of
    unstable or runaway results (factors are capped).
    ==========================================
    */

    function macrosForGrams(foodId, grams) {
        const food = FOODS[foodId];
        if (!food) return { kcal: 0, protein: 0, carbs: 0, fat: 0 };
        const factor = grams / 100;
        return {
            kcal: food.kcal100g * factor,
            protein: food.protein100g * factor,
            carbs: food.carbs100g * factor,
            fat: food.fat100g * factor
        };
    }

    function clamp(value, min, max) {
        return Math.min(max, Math.max(min, value));
    }

    function collectCategoryItems(mealPlan, category) {
        const items = [];
        mealPlan.forEach(function (meal) {
            meal.items.forEach(function (item) {
                const food = FOODS[item.foodId];
                if (food && food.category === category) {
                    items.push(item);
                }
            });
        });
        return items;
    }

    function sumMacroExcludingCategory(mealPlan, category, macroKey) {
        let sum = 0;
        mealPlan.forEach(function (meal) {
            meal.items.forEach(function (item) {
                const food = FOODS[item.foodId];
                if (food && food.category !== category) {
                    sum += item[macroKey];
                }
            });
        });
        return sum;
    }

    function scaleCategoryToTarget(mealPlan, category, macroKey, dailyTarget) {

        const items = collectCategoryItems(mealPlan, category);
        if (items.length === 0) return;

        const fixedContribution = sumMacroExcludingCategory(mealPlan, category, macroKey);
        const remainingTarget = Math.max(0, dailyTarget - fixedContribution);

        const currentCategorySum = items.reduce(function (s, it) { return s + it[macroKey]; }, 0);
        if (currentCategorySum <= 0) return;

        // Capped so a single category can't be scaled to absurd extremes
        // for edge-case profiles (e.g. very high protein targets).
        let factor = clamp(remainingTarget / currentCategorySum, 0.4, 2.5);

        items.forEach(function (item) {
            const newGrams = Math.max(1, Math.round(item.grams * factor));
            const macros = macrosForGrams(item.foodId, newGrams);
            item.grams = newGrams;
            item.kcal = Math.round(macros.kcal);
            item.protein = Math.round(macros.protein * 10) / 10;
            item.carbs = Math.round(macros.carbs * 10) / 10;
            item.fat = Math.round(macros.fat * 10) / 10;
        });
    }

    function recomputeMealTotals(mealPlan) {
        mealPlan.forEach(function (meal) {
            meal.kcal = Math.round(meal.items.reduce(function (s, i) { return s + i.kcal; }, 0));
            meal.protein = Math.round(meal.items.reduce(function (s, i) { return s + i.protein; }, 0));
            meal.carbs = Math.round(meal.items.reduce(function (s, i) { return s + i.carbs; }, 0));
            meal.fat = Math.round(meal.items.reduce(function (s, i) { return s + i.fat; }, 0));
        });
    }

    const Optimizer = {};

    Optimizer.optimize = function (mealPlan, targets) {

        // Multiple passes let each macro's scaling account for how
        // much the OTHER categories shifted in the previous round —
        // a single pass can overshoot when targets are far from the
        // baseline (e.g. a high-carb bulking target drags protein
        // along with it, since oats/quinoa/rice cakes aren't 100%
        // carb). 3 rounds converges to a stable, accurate result.
        const ROUNDS = 3;

        for (let round = 0; round < ROUNDS; round++) {
            scaleCategoryToTarget(mealPlan, "protein", "protein", targets.protein);
            scaleCategoryToTarget(mealPlan, "carb", "carbs", targets.carbs);
            scaleCategoryToTarget(mealPlan, "fat", "fat", targets.fat);
        }

        recomputeMealTotals(mealPlan);

        return mealPlan;
    };

    window.VORYNEX.optimizer = Optimizer;
    console.log("Optimizer exported.");

})();